import {
  FolderIcon,
  InventoryIcon,
  LocationIcon,
} from '../../../components/ui/icons';
import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { BrandLogo } from '../../../components/ui/BrandLogo';
import { CollectionSession } from '@qkindex/contracts';
import { LoadingState } from '../../../components/ui/States';
import { CollectionResolutionStatus } from '../collectionSessionService';
import { collectionManagementErrorMessage } from '../collectionManagementService';
import { userProfileResolverService } from '../../../services/userProfileResolverService';

const roleLabel = (
  role: 'owner' | 'administrator' | 'editor' | 'viewer',
): string => `${role.slice(0, 1).toUpperCase()}${role.slice(1)}`;

const unsafeAccessibleNameCodePoint = (codePoint: number): boolean =>
  codePoint <= 0x1f ||
  (codePoint >= 0x7f && codePoint <= 0x9f) ||
  codePoint === 0x61c ||
  codePoint === 0x200e ||
  codePoint === 0x200f ||
  (codePoint >= 0x2028 && codePoint <= 0x202e) ||
  (codePoint >= 0x2066 && codePoint <= 0x2069);

const accessibleCollectionName = (name: string): string =>
  Array.from(name, (character) =>
    unsafeAccessibleNameCodePoint(character.codePointAt(0) || 0)
      ? ' '
      : character,
  )
    .join('')
    .replace(/\s+/gu, ' ')
    .trim();

function CollectionLabel({
  userId,
  collection,
}: {
  userId: string;
  collection: CollectionSession['availableCollections']['items'][number];
}) {
  const identity = JSON.stringify([
    userId,
    collection.collectionId,
    collection.ownerUserId,
    collection.membershipVersion,
    collection.role,
  ]);
  const [resolvedOwner, setResolvedOwner] = useState<{
    identity: string;
    username: string | null;
  }>();
  const ownerUsername =
    collection.ownerUsername ||
    (resolvedOwner?.identity === identity ? resolvedOwner.username : undefined);
  useEffect(() => {
    if (
      collection.role === 'owner' ||
      collection.ownerUsername ||
      !collection.ownerUserId
    )
      return;
    let active = true;
    void userProfileResolverService
      .resolveUsername(
        collection.collectionId,
        collection.ownerUserId,
        collection.membershipVersion,
      )
      .then((username) => {
        if (active) setResolvedOwner({ identity, username });
      })
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, [
    identity,
    collection.collectionId,
    collection.ownerUserId,
    collection.membershipVersion,
    collection.role,
    collection.ownerUsername,
  ]);
  const role = roleLabel(collection.role);
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        gap: 0.5,
        alignItems: 'baseline',
        minWidth: 0,
        width: '100%',
      }}
    >
      <Box
        component="bdi"
        dir="auto"
        aria-label={`Collection name: ${accessibleCollectionName(collection.name)}`}
        sx={{
          flex: '1 1 auto',
          minWidth: 0,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {collection.name}
      </Box>
      {collection.role !== 'owner' && ownerUsername && (
        <Box
          component="bdi"
          dir="ltr"
          aria-label={`Owner: @${ownerUsername}`}
          sx={{ flexShrink: 0, color: 'text.secondary' }}
        >
          @{ownerUsername}
        </Box>
      )}
      <Box component="span" aria-hidden="true" sx={{ flexShrink: 0 }}>
        —
      </Box>
      <Box
        component="bdi"
        dir="ltr"
        aria-label={`Role: ${role}`}
        sx={{ flexShrink: 0 }}
      >
        {role}
      </Box>
      {collection.lifecycle === 'transfer-pending' && (
        <>
          <Box component="span" aria-hidden="true" sx={{ flexShrink: 0 }}>
            —
          </Box>
          <Box
            component="bdi"
            dir="ltr"
            aria-label="Status: Transfer pending"
            sx={{ flexShrink: 0 }}
          >
            Transfer pending
          </Box>
        </>
      )}
    </Box>
  );
}

function CollectionNameForm({
  label,
  action,
  onSubmit,
}: {
  label: string;
  action: string;
  onSubmit(name: string): Promise<void>;
}) {
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (busy) return;
    setBusy(true);
    setError(undefined);
    try {
      await onSubmit(name);
      setName('');
    } catch (submitError) {
      setError(collectionManagementErrorMessage(submitError));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box
      component="form"
      onSubmit={(event) => {
        void submit(event);
      }}
      sx={{ mt: 2 }}
    >
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
        <TextField
          fullWidth
          size="small"
          label={label}
          value={name}
          inputProps={{ maxLength: 320 }}
          onChange={(event) => setName(event.target.value)}
          disabled={busy}
          error={Boolean(error)}
          helperText={error}
        />
        <Button
          type="submit"
          variant="contained"
          disabled={busy || !name.trim()}
        >
          {busy ? 'Saving…' : action}
        </Button>
      </Stack>
    </Box>
  );
}

function JoinCollectionForm() {
  const [value, setValue] = useState('');
  const [error, setError] = useState<string>();

  const extractToken = (raw: string): string | null => {
    const trimmed = raw.trim();
    if (!trimmed) return null;
    try {
      if (trimmed.includes('invite=')) {
        const url = new URL(
          trimmed.startsWith('http') ? trimmed : `http://localhost/${trimmed}`,
        );
        if (url.searchParams.has('invite')) return null;
        const token = new URLSearchParams(url.hash.slice(1)).get('invite');
        if (token && /^[A-Za-z0-9_-]{32,512}$/u.test(token)) return token;
      }
    } catch {
      // Fall back to direct token format matching
    }
    if (/^[A-Za-z0-9_-]{32,512}$/u.test(trimmed)) return trimmed;
    return null;
  };

  const handleJoin = (event: FormEvent) => {
    event.preventDefault();
    setError(undefined);
    const token = extractToken(value);
    if (!token) {
      setError('Please enter a valid invitation link or 32-character token.');
      return;
    }
    setValue('');
    window.location.hash = `invite=${token}`;
  };

  return (
    <Box
      component="form"
      onSubmit={handleJoin}
      sx={{ mt: 3, pt: 2, borderTop: 1, borderColor: 'divider' }}
    >
      <Typography variant="subtitle2" color="text.primary" fontWeight={700}>
        Joining a shared collection?
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Open the invitation link sent by your collection owner while logged in,
        or paste your link or token below:
      </Typography>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
        <TextField
          fullWidth
          size="small"
          label="Invitation link or token"
          placeholder="Paste invitation link or token"
          value={value}
          onChange={(event) => setValue(event.target.value)}
          error={Boolean(error)}
          helperText={error}
        />
        <Button type="submit" variant="outlined" disabled={!value.trim()}>
          Join collection
        </Button>
      </Stack>
    </Box>
  );
}

function CollectionEducationalGuide() {
  return (
    <Card
      variant="outlined"
      sx={{ mt: 3, borderRadius: 1, bgcolor: 'background.paper' }}
    >
      <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
        <Typography
          variant="subtitle2"
          color="text.secondary"
          fontWeight={700}
          textTransform="uppercase"
          letterSpacing={0.5}
          sx={{ mb: 1.5 }}
        >
          QuickIndex Basics
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <Stack direction="row" spacing={1.5} alignItems="flex-start">
              <FolderIcon color="primary" sx={{ mt: 0.25 }} />
              <Box>
                <Typography variant="subtitle2" fontWeight={700}>
                  Collections
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Workspaces to group inventory for your household, workshop,
                  business, or team.
                </Typography>
              </Box>
            </Stack>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Stack direction="row" spacing={1.5} alignItems="flex-start">
              <InventoryIcon color="primary" sx={{ mt: 0.25 }} />
              <Box>
                <Typography variant="subtitle2" fontWeight={700}>
                  Items
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Your tracked assets with quantities, descriptions, photos, and
                  tags.
                </Typography>
              </Box>
            </Stack>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Stack direction="row" spacing={1.5} alignItems="flex-start">
              <LocationIcon color="primary" sx={{ mt: 0.25 }} />
              <Box>
                <Typography variant="subtitle2" fontWeight={700}>
                  Locations
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Physical or logical spaces (e.g. Garage, Shelf 3, Bin B) where
                  items reside.
                </Typography>
              </Box>
            </Stack>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
}

function LoadMoreCollections({ onLoadMore }: { onLoadMore(): Promise<void> }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const load = async () => {
    if (busy) return;
    setBusy(true);
    setError(undefined);
    try {
      await onLoadMore();
    } catch {
      setError(
        'Unable to load more collections. Check your connection and try again.',
      );
    } finally {
      setBusy(false);
    }
  };
  return (
    <Box sx={{ mt: 1 }}>
      <Button
        variant="outlined"
        disabled={busy}
        onClick={() => {
          void load();
        }}
      >
        {busy ? 'Loading…' : 'Load more collections'}
      </Button>
      {error && (
        <Alert severity="error" sx={{ mt: 1 }}>
          {error}
        </Alert>
      )}
    </Box>
  );
}

export function CollectionSessionGate({
  status,
  session,
  onRetry,
  onSelect,
  onCreate,
  onRename,
  onLoadMore,
  hideCollectionControls = false,
  children,
}: {
  status: CollectionResolutionStatus;
  session?: CollectionSession;
  onRetry: () => void;
  onSelect: (collectionId: string) => void;
  onCreate?: (name: string) => Promise<void>;
  onRename?: (name: string) => Promise<void>;
  onLoadMore?: () => Promise<void>;
  hideCollectionControls?: boolean;
  children: React.ReactNode;
}) {
  if (status === 'idle' || status === 'loading') {
    return <LoadingState label="Loading collections…" />;
  }

  if (status !== 'ready') {
    const copy = {
      empty: {
        title: 'No collections available',
        description: 'Create your free collection to start adding inventory.',
        severity: 'info' as const,
      },
      revoked: {
        title: 'Collection access revoked',
        description:
          'Your saved collection is no longer available to this account.',
        severity: 'warning' as const,
      },
      unavailable: {
        title: 'Collection unavailable',
        description: 'The selected collection is currently unavailable.',
        severity: 'warning' as const,
      },
      error: {
        title: 'Unable to load collections',
        description: 'Check your connection and try again.',
        severity: 'error' as const,
      },
    }[status];
    return (
      <Box sx={{ maxWidth: 640, mx: 'auto', px: 2, py: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <BrandLogo />
        </Box>
        <Alert
          severity={copy.severity}
          action={
            <Button color="inherit" onClick={onRetry}>
              Retry
            </Button>
          }
        >
          <Typography fontWeight={700}>{copy.title}</Typography>
          {copy.description}
        </Alert>
        {status === 'empty' && onCreate && (
          <CollectionNameForm
            label="Collection name"
            action="Create collection"
            onSubmit={onCreate}
          />
        )}
        {status === 'empty' && <JoinCollectionForm />}
        {session && session.availableCollections.items.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <FormControl fullWidth size="small">
              <InputLabel id="fallback-collection-label">
                Choose another collection
              </InputLabel>
              <Select
                labelId="fallback-collection-label"
                value=""
                label="Choose another collection"
                onChange={(event) => onSelect(event.target.value)}
              >
                {session.availableCollections.items.map(
                  (
                    collection: CollectionSession['availableCollections']['items'][number],
                  ) => (
                    <MenuItem
                      key={collection.collectionId}
                      value={collection.collectionId}
                    >
                      <CollectionLabel
                        userId={session.userId}
                        collection={collection}
                      />
                    </MenuItem>
                  ),
                )}
              </Select>
            </FormControl>
          </Box>
        )}
        {onLoadMore && <LoadMoreCollections onLoadMore={onLoadMore} />}
        <CollectionEducationalGuide />
      </Box>
    );
  }

  const collections = session?.availableCollections.items || [];
  return (
    <>
      {!hideCollectionControls &&
        collections.length > 0 &&
        session?.activeCollectionId && (
          <Box sx={{ px: 2, pt: 1, maxWidth: 420 }}>
            <FormControl fullWidth size="small">
              <InputLabel id="active-collection-label">Collection</InputLabel>
              <Select
                labelId="active-collection-label"
                value={session.activeCollectionId}
                label="Collection"
                onChange={(event) => onSelect(event.target.value)}
              >
                {collections.map(
                  (
                    collection: CollectionSession['availableCollections']['items'][number],
                  ) => (
                    <MenuItem
                      key={collection.collectionId}
                      value={collection.collectionId}
                    >
                      <CollectionLabel
                        userId={session.userId}
                        collection={collection}
                      />
                    </MenuItem>
                  ),
                )}
              </Select>
            </FormControl>
            {(session.activeRole === 'owner' ||
              session.activeRole === 'administrator') &&
              onRename && (
                <CollectionNameForm
                  label="New collection name"
                  action="Rename"
                  onSubmit={onRename}
                />
              )}
            {onLoadMore && <LoadMoreCollections onLoadMore={onLoadMore} />}
          </Box>
        )}
      {children}
    </>
  );
}
